import { useState } from 'react';

import { ChatbotAnswer, ChatbotResponse, FilterData } from '../types';
import { filterAllowedKeys } from '../utils/filterUtils';

import { useUrlFilterNavigator } from './urlFilterNavigator';

export const useFilterManager = () => {
  const { applyFilters, getNonEmptyFilters, resetFilters } =
    useUrlFilterNavigator();

  const [pendingFilters, setPendingFilters] = useState<FilterData | null>(null);
  const [pendingFunctionCall, setPendingFunctionCall] = useState<any>(null);

  const getCurrentFilters = () => getNonEmptyFilters();
  const currentFilters = getCurrentFilters();
  const applyPendingFilters = async (cancelled?: boolean) => {
    if (!pendingFilters || !pendingFunctionCall) {
      return null;
    }
    const normalizedCurrent = filterAllowedKeys(currentFilters, false);
    const normalizedPending = filterAllowedKeys(pendingFilters, false);
    const mergedFilters = { ...normalizedCurrent, ...normalizedPending };
    try {
      if (!cancelled) {
        applyFilters(
          mergedFilters as Record<
            string,
            string | number | boolean | string[] | null | undefined
          >
        );
      }
      setPendingFilters(cancelled ? pendingFilters : null);
      setPendingFunctionCall(null);
    } catch (err) {
      console.error('Failed to apply filters:', err);
      return null;
    }
  };

  const processResponseForFunctionCalls = async (response: ChatbotResponse) => {
    let parsedAnswer: ChatbotAnswer;
    try {
      if (!response?.data?.answer) {
        return { isFunction: false, response };
      }
      parsedAnswer = JSON.parse(response.data.answer);
    } catch (_e) {
      return { isFunction: false, response };
    }

    const { tool, parameters } = parsedAnswer || {};
    if (
      tool !== 'createFilterParams' ||
      !parameters ||
      Object.keys(parameters).length === 0
    ) {
      return { isFunction: false, response };
    }

    const mergedFilters = { ...currentFilters, ...parameters };
    setPendingFilters(mergedFilters);
    setPendingFunctionCall({
      name: tool,
      arguments: JSON.stringify(parameters),
      call_id: response.context_id,
    });
    return {
      isFunction: true,
      filters: mergedFilters,
    };
  };

  const clearPendingFilters = () => {
    setPendingFilters(null);
    setPendingFunctionCall(null);
  };

  const handleResetFilters = async () => {
    setPendingFilters(null);
    setPendingFunctionCall(null);
    resetFilters();
  };

  return {
    getCurrentFilters,
    processResponseForFunctionCalls,
    pendingFilters,
    pendingFunctionCall,
    applyPendingFilters,
    clearPendingFilters,
    setPendingFilters,
    setPendingFunctionCall,
    handleResetFilters,
  };
};
